import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Stack, Input, Textarea, FormControl, FormLabel, Alert, Sheet, Container, Grid, CircularProgress, LinearProgress, Select, Option, Checkbox } from '@mui/joy';
import { useNavigate, useParams } from 'react-router-dom';
import { getEmployer, getEmployerPosition, upsertEmployerPosition, deleteEmployerPosition, getGlobalPositions, getAllApplicants } from '../../../apiconfig/api';
import { uploadVideoWithPresignedUrl } from '../../../components/steps/functions/uploadVideoWithPresignedUrl';
import { getFileExtensionFromMime } from '../../../utils/getFileExtension';
import { upsertMedia } from '../../../apiconfig/api';
import { v4 as uuidv4 } from 'uuid';
import useDeviceInfo from '../../../hooks/useDeviceInfo';
import GeekPlayer from '../../../components/GeekPlayer';

const EditPositionListingPage: React.FC = () => {
  const navigate = useNavigate();
  const { employer_slug, position_slug } = useParams();
  
  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [applicantCount, setApplicantCount] = useState(0);

  // Form state
  const [positionTitle, setPositionTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [globalPositionId, setGlobalPositionId] = useState('');
  const [employmentType, setEmploymentType] = useState<'part-time' | 'full-time'>('full-time');
  const [wageType, setWageType] = useState<'hourly' | 'salary'>('hourly');
  const [wageMin, setWageMin] = useState('');
  const [wageMax, setWageMax] = useState('');
  const [isPartTime, setIsPartTime] = useState(false);
  const [isFullTime, setIsFullTime] = useState(false);
  const [isHourly, setIsHourly] = useState(false);
  const [isSalary, setIsSalary] = useState(false);
  const [hourlyMin, setHourlyMin] = useState('');
  const [hourlyMax, setHourlyMax] = useState('');
  const [salaryMin, setSalaryMin] = useState('');
  const [salaryMax, setSalaryMax] = useState('');
  const [receivesTips, setReceivesTips] = useState(false);
  const [positionDescription, setPositionDescription] = useState('');
  const [positionMedia, setPositionMedia] = useState<File | null>(null);
  const [visibility, setVisibility] = useState<'public' | 'unlisted' | 'inactive'>('public');
  
  // Store the actual IDs for API calls
  const [employerId, setEmployerId] = useState<string>('');
  const [positionId, setPositionId] = useState<string>('');

  // Video upload state
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedVideoKey, setUploadedVideoKey] = useState<string>('');
  const [showExistingVideo, setShowExistingVideo] = useState(false);
  const [existingMediaId, setExistingMediaId] = useState<string | null>(null);
  
  const deviceInfo = useDeviceInfo();

  // Global positions state
  const [globalPositions, setGlobalPositions] = useState<string[]>([]);
  const [loadingGlobalPositions, setLoadingGlobalPositions] = useState(true);

  // Fetch global positions
  useEffect(() => {
    const fetchGlobalPositions = async () => {
      try {
        setLoadingGlobalPositions(true);
        const response = await getGlobalPositions();
        setGlobalPositions(response.data?.globalPositions || []);
      } catch (err) {
        console.error("Error fetching global positions:", err);
        setError("Failed to load global positions. Please try again.");
      } finally {
        setLoadingGlobalPositions(false);
      }
    };

    fetchGlobalPositions();
  }, []);

  // Initialize form with position data
  useEffect(() => {
    const fetchPositionData = async () => {
      if (!employer_slug || !position_slug) {
        setError("Missing employer or position information");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch position data
        const positionResponse = await getEmployerPosition(employer_slug, position_slug);
        const position = positionResponse.data.position;

        // Store the actual IDs for API calls
        setEmployerId(position.employerId);
        setPositionId(position.id);
        
        // Initialize form with position data
        setPositionTitle(position.displayNameOverride || '');
        setSlug(position.slugOverride || '');
        setGlobalPositionId(position.positionId || '');
        setEmploymentType(position.class || 'full-time');
        setWageType(position.wageType || 'hourly');
        setWageMin(position.minWageCents ? (position.minWageCents / 100).toString() : '');
        setWageMax(position.maxWageCents ? (position.maxWageCents / 100).toString() : '');
        setIsPartTime(position.isPartTime || false);
        setIsFullTime(position.isFullTime || false);
        setIsHourly(position.isHourly || false);
        setIsSalary(position.isSalary || false);
        setHourlyMin(position.minHourlyCents ? (position.minHourlyCents / 100).toString() : '');
        setHourlyMax(position.maxHourlyCents ? (position.maxHourlyCents / 100).toString() : '');
        setSalaryMin(position.minSalaryCents ? (position.minSalaryCents / 100).toString() : '');
        setSalaryMax(position.maxSalaryCents ? (position.maxSalaryCents / 100).toString() : '');
        setReceivesTips(position.receivesTips || false);
        setPositionDescription(position.description || '');
        setVisibility(position.visibility || 'public');
        
        // Show existing video if available
        if (position.mediaId) {
          setShowExistingVideo(true);
          setExistingMediaId(position.mediaId);
        }

        // Fetch person count for this position
        try {
          const applicantsResponse = await getAllApplicants({
            submittedToEmployerPositionIds: [position.id],
            page: 1,
            per_page: 1
          });
          setApplicantCount(applicantsResponse.data.total || 0);
        } catch (err) {
          console.error('Failed to fetch applicant count:', err);
          setApplicantCount(0);
        }

      } catch (err) {
        console.error("Error fetching position data:", err);
        setError("Failed to load position information. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchPositionData();
  }, [employer_slug, position_slug]);

  const processVideoFile = async (file: File) => {
    if (!file.type.startsWith('video/')) {
      setError('Please select a valid video file.');
      return;
    }

    setPositionMedia(file);
    // Create local URL for preview
    const url = URL.createObjectURL(file);
    setVideoUrl(url);
    setShowExistingVideo(false);
    
    // Upload video immediately
    await uploadVideo(file);
  };

  const handleVideoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      await processVideoFile(file);
    }
  };

  const uploadVideo = async (file: File) => {
    try {
      setIsUploading(true);
      setUploadProgress(0);

      const extension = getFileExtensionFromMime(
        file.type || "video/mp4"
      );

      const uploadFile = new File([file], `position-media.${extension}`, {
        type: file.type || "video/mp4",
      });

      // Create media metadata and get upload URL
      const mediaId = uuidv4();
      const metadataPayload = {
        id: mediaId,
        original_file_key: uploadFile.name,
        original_file_mime_type: uploadFile.type,
        metadata: {
          metadata_version: new Date().toISOString().split("T")[0],
          source: "upload",
          upload_device: {
            name:
              deviceInfo.device.vendor && deviceInfo.device.model
                ? `${deviceInfo.device.vendor} ${deviceInfo.device.model}`
                : null,
            manufacturer: deviceInfo.device.vendor ?? null,
            model: deviceInfo.device.model ?? null,
            version: deviceInfo.browser.version ?? null,
            os: deviceInfo.os.name ?? null,
            os_version: deviceInfo.os.version ?? null,
          },
        },
      };

      const mediaResult = await upsertMedia(mediaId, metadataPayload);
      const uploadUrl = (mediaResult as any).data?.uploadUrl || (mediaResult as any).uploadUrl;

      if (!uploadUrl) {
        throw new Error("Upload URL is missing from media metadata response.");
      }

      // Upload the video using the URL from media metadata
      await uploadVideoWithPresignedUrl({
        file: uploadFile,
        uploadUrl: uploadUrl,
        onProgress: (progress) => {
          console.log('Upload progress:', progress);
          setUploadProgress(progress);
        },
      });

      // Store the media ID as the position media UUID
      setUploadedVideoKey(mediaId);

      setUploadProgress(100);
      setIsUploading(false);
    } catch (error) {
      console.error("Video upload failed:", error);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSave = async () => {
    if (!employerId || !positionId) {
      setError("Missing employer or position information");
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const payload: any = {
        displayNameOverride: positionTitle,
        slugOverride: slug,
        positionId: globalPositionId,
        isPartTime: isPartTime,
        isFullTime: isFullTime,
        isHourly: isHourly,
        isSalary: isSalary,
        minHourlyCents: parseFloat(hourlyMin) ? Math.round(parseFloat(hourlyMin) * 100) : null,
        maxHourlyCents: parseFloat(hourlyMax) ? Math.round(parseFloat(hourlyMax) * 100) : null,
        minSalaryCents: parseFloat(salaryMin) ? Math.round(parseFloat(salaryMin) * 100) : null,
        maxSalaryCents: parseFloat(salaryMax) ? Math.round(parseFloat(salaryMax) * 100) : null,
        receivesTips: receivesTips,
        description: positionDescription,
        visibility: visibility,
      };

      if (uploadedVideoKey === 'remove') {
        payload.mediaId = null;
      } else if (uploadedVideoKey) {
        payload.mediaId = uploadedVideoKey;
      }

      await upsertEmployerPosition(employerId, positionId, payload);
      
      // Navigate based on browser history
      if (window.history.length > 1) {
        navigate(-1); // Go back to previous page
      } else {
        navigate('/hiring/positions'); // Default fallback
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update position.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!employerId || !positionId) {
      setError("Missing employer or position information");
      return;
    }

    if (applicantCount > 0) {
      setError("Cannot delete position with active people");
      return;
    }

    try {
      setDeleting(true);
      setError(null);

      // Delete the position
      await deleteEmployerPosition(employerId, positionId);
      
      // Navigate back to positions list
      navigate('/hiring/positions');
      
    } catch (err: any) {
      setError(err.message || 'Failed to delete position.');
    } finally {
      setDeleting(false);
    }
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      await processVideoFile(files[0]);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const validateSlug = (slug: string) => {
    if (slug.length > 100) return 'URL slug must be 100 characters or fewer';
    if (!/^[a-z0-9-]+$/.test(slug)) return 'URL slug can only contain lowercase letters, numbers, and hyphens';
    return null;
  };

  const slugError = validateSlug(slug);

  const inputStyles = {
    height: '56px',
    backgroundColor: '#ECEFF3',
    borderRadius: '12px',
    border: 'none',
    fontSize: '1.125rem',
    color: '#1A1B1D',
    '--Input-focusedHighlight': 'transparent !important',
    '&::placeholder': {
      color: '#7B7F85 !important',
      opacity: 1,
    },
    '&:hover': {
      backgroundColor: '#ECEFF3',
    },
    '&:focus': {
      outline: 'none',
    },
    '&:focus-within': {
      backgroundColor: '#ECEFF3',
    },
  };

  const textareaStyles = {
    backgroundColor: '#ECEFF3',
    borderRadius: '12px',
    border: 'none',
    fontSize: '1.125rem',
    color: '#1A1B1D',
    '--Textarea-focusedHighlight': 'transparent !important',
    '&::placeholder': {
      color: '#7B7F85 !important',
      opacity: 1,
    },
    '&:hover': {
      backgroundColor: '#ECEFF3',
    },
    '&:focus': {
      outline: 'none',
    },
    '&:focus-within': {
      backgroundColor: '#ECEFF3',
    },
  };

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: "100vh", 
        bgcolor: "#f4f4f4",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}>
        <CircularProgress size="lg" />
      </Box>
    );
  }

  if (error && !positionTitle) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f4f4f4',
          padding: '20px',
        }}
      >
        <Typography level="h2" sx={{ fontWeight: 700 }}>
          Position not found
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f4f4', py: 4 }}>
      <Container maxWidth="lg">
        <Typography level="h2" sx={{ mb: 3, fontWeight: 700 }}>
          Edit Position
        </Typography>

        <Sheet
          sx={{
            width: '100%',
            borderRadius: '16px',
            backgroundColor: '#ffffff',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            padding: '40px',
          }}
        >
          {(error || success) && (
            <Alert 
              color={error ? "danger" : "success"} 
              sx={{ mb: 3 }}
            >
              {error || success}
            </Alert>
          )}

          <Grid container spacing={4}>
            {/* Left Column - Form Fields */}
            <Grid xs={12} md={8}>
              <Stack spacing={3}>
                {/* Position Title */}
                <FormControl>
                  <FormLabel sx={{ fontWeight: 500, fontSize: '1rem', color: '#1A1B1D', marginBottom: '8px' }}>
                    Position Title
                  </FormLabel>
                  <Input
                    value={positionTitle}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPositionTitle(e.target.value)}
                    placeholder="e.g. Senior Software Engineer"
                    sx={inputStyles}
                  />
                </FormControl>

                {/* Visibility */}
                <FormControl>
                  <FormLabel sx={{ fontWeight: 500, fontSize: '1rem', color: '#1A1B1D', marginBottom: '8px' }}>
                    Visibility
                  </FormLabel>
                  <Select
                    value={visibility}
                    onChange={(_, value) => setVisibility(value as 'public' | 'unlisted' | 'inactive')}
                    sx={inputStyles}
                  >
                    <Option value="public">Public - everyone can find and apply to your post</Option>
                    <Option value="unlisted">Unlisted - only people with the link can find and apply to your post</Option>
                    <Option value="inactive">Inactive - only you can see this post</Option>
                  </Select>
                </FormControl>

                {/* URL Slug */}
                <FormControl error={!!slugError}>
                  <FormLabel sx={{ fontWeight: 500, fontSize: '1rem', color: '#1A1B1D', marginBottom: '8px' }}>
                    URL
                  </FormLabel>
                  <Input
                    value={slug}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSlug(e.target.value.toLowerCase())}
                    placeholder="e.g. senior-software-engineer"
                    sx={inputStyles}
                    startDecorator={"https://geekapp.co/apply/" + employer_slug + "/"}
                  />
                  {slugError && (
                    <Typography level="body-sm" color="danger" marginTop="5px">
                      {slugError}
                    </Typography>
                  )}
                </FormControl>

                {/* Role */}
                <FormControl>
                  <FormLabel sx={{ fontWeight: 500, fontSize: '1rem', color: '#1A1B1D', marginBottom: '8px' }}>
                    Role
                  </FormLabel>
                  <Select
                    value={globalPositionId}
                    onChange={(_, value) => setGlobalPositionId(value || '')}
                    placeholder={loadingGlobalPositions ? "Loading roles..." : "Select a role"}
                    disabled={loadingGlobalPositions}
                    sx={inputStyles}
                  >
                    {globalPositions.map((position: any) => (
                      <Option key={position.id} value={position.id}>
                        {position.displayName}
                      </Option>
                    ))}
                  </Select>
                </FormControl>

                {/* Employment Type */}
                <FormControl>
                  <FormLabel sx={{ fontWeight: 500, fontSize: '1rem', color: '#1A1B1D', marginBottom: '8px' }}>
                    Employment Type
                  </FormLabel>
                  <Stack direction="row" spacing={2}>
                    <Box
                      onClick={() => setIsFullTime(!isFullTime)}
                      sx={{
                        flex: 1,
                        height: '80px',
                        padding: '16px',
                        borderRadius: '12px',
                        backgroundColor: isFullTime ? '#F0F2F5' : '#F8F9FA',
                        border: isFullTime ? '2px solid #2e2d7e' : '1px solid #E5E7EB',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          borderColor: '#2e2d7e',
                          backgroundColor: isFullTime ? '#E8EAED' : '#F0F2F5',
                        },
                      }}
                    >
                      <Typography level="h4" sx={{ fontWeight: 600, mb: 1, color: '#1A1B1D', fontSize: '1.125rem' }}>
                        Full-time
                      </Typography>
                      <Typography level="body-sm" sx={{ color: '#7B7F85', lineHeight: 1.3, fontSize: '0.875rem' }}>
                        Permanent positions with 40+ hours per week
                      </Typography>
                    </Box>
                    
                    <Box
                      onClick={() => setIsPartTime(!isPartTime)}
                      sx={{
                        flex: 1,
                        height: '80px',
                        padding: '16px',
                        borderRadius: '12px',
                        backgroundColor: isPartTime ? '#F0F2F5' : '#F8F9FA',
                        border: isPartTime ? '2px solid #2e2d7e' : '1px solid #E5E7EB',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          borderColor: '#2e2d7e',
                          backgroundColor: isPartTime ? '#E8EAED' : '#F0F2F5',
                        },
                      }}
                    >
                      <Typography level="h4" sx={{ fontWeight: 600, mb: 1, color: '#1A1B1D', fontSize: '1.125rem' }}>
                        Part-time
                      </Typography>
                      <Typography level="body-sm" sx={{ color: '#7B7F85', lineHeight: 1.3, fontSize: '0.875rem' }}>
                        Flexible positions with fewer hours
                      </Typography>
                    </Box>
                  </Stack>
                </FormControl>

                {/* Wage Type */}
                <FormControl>
                  <FormLabel sx={{ fontWeight: 500, fontSize: '1rem', color: '#1A1B1D', marginBottom: '8px' }}>
                    Wage Type
                  </FormLabel>
                  <Stack spacing={2}>
                    <Box
                      onClick={() => setIsSalary(!isSalary)}
                      sx={{
                        width: '100%',
                        padding: '20px',
                        borderRadius: '12px',
                        backgroundColor: isSalary ? '#F0F2F5' : '#F8F9FA',
                        border: isSalary ? '2px solid #2e2d7e' : '1px solid #E5E7EB',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          borderColor: '#2e2d7e',
                          backgroundColor: isSalary ? '#E8EAED' : '#F0F2F5',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: isSalary ? 2 : 0 }}>
                        <Typography level="h4" sx={{ fontWeight: 600, color: '#1A1B1D', fontSize: '1.125rem' }}>
                          Salary
                        </Typography>
                        <Typography level="body-sm" sx={{ color: '#7B7F85', fontSize: '0.875rem', ml: 2 }}>
                          Annual salary compensation
                        </Typography>
                      </Box>
                      
                      {isSalary && (
                        <Grid container spacing={2}>
                          <Grid xs={6}>
                            <FormControl>
                              <FormLabel sx={{ fontWeight: 500, fontSize: '0.875rem', color: '#1A1B1D', marginBottom: '4px' }}>
                                Min Annual Salary
                              </FormLabel>
                              <Input
                                value={salaryMin}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSalaryMin(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                placeholder="e.g. 50000"
                                type="number"
                                sx={{
                                  height: '48px',
                                  backgroundColor: '#FFFFFF',
                                  borderRadius: '8px',
                                  border: '1px solid #E5E7EB',
                                  fontSize: '1rem',
                                  color: '#1A1B1D',
                                  '--Input-focusedHighlight': 'transparent !important',
                                  '&::placeholder': {
                                    color: '#7B7F85 !important',
                                    opacity: 1,
                                  },
                                  '&:hover': {
                                    backgroundColor: '#FFFFFF',
                                  },
                                  '&:focus': {
                                    outline: 'none',
                                  },
                                  '&:focus-within': {
                                    backgroundColor: '#FFFFFF',
                                  },
                                }}
                                startDecorator="$"
                              />
                            </FormControl>
                          </Grid>
                          <Grid xs={6}>
                            <FormControl>
                              <FormLabel sx={{ fontWeight: 500, fontSize: '0.875rem', color: '#1A1B1D', marginBottom: '4px' }}>
                                Max Annual Salary
                              </FormLabel>
                              <Input
                                value={salaryMax}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSalaryMax(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                placeholder="e.g. 75000"
                                type="number"
                                sx={{
                                  height: '48px',
                                  backgroundColor: '#FFFFFF',
                                  borderRadius: '8px',
                                  border: '1px solid #E5E7EB',
                                  fontSize: '1rem',
                                  color: '#1A1B1D',
                                  '--Input-focusedHighlight': 'transparent !important',
                                  '&::placeholder': {
                                    color: '#7B7F85 !important',
                                    opacity: 1,
                                  },
                                  '&:hover': {
                                    backgroundColor: '#FFFFFF',
                                  },
                                  '&:focus': {
                                    outline: 'none',
                                  },
                                  '&:focus-within': {
                                    backgroundColor: '#FFFFFF',
                                  },
                                }}
                                startDecorator="$"
                              />
                            </FormControl>
                          </Grid>
                        </Grid>
                      )}
                    </Box>
                  
                    <Box
                      onClick={() => setIsHourly(!isHourly)}
                      sx={{
                        width: '100%',
                        padding: '20px',
                        borderRadius: '12px',
                        backgroundColor: isHourly ? '#F0F2F5' : '#F8F9FA',
                        border: isHourly ? '2px solid #2e2d7e' : '1px solid #E5E7EB',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          borderColor: '#2e2d7e',
                          backgroundColor: isHourly ? '#E8EAED' : '#F0F2F5',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: isHourly ? 2 : 0 }}>
                        <Typography level="h4" sx={{ fontWeight: 600, color: '#1A1B1D', fontSize: '1.125rem' }}>
                          Hourly
                        </Typography>
                        <Typography level="body-sm" sx={{ color: '#7B7F85', fontSize: '0.875rem', ml: 2 }}>
                          Pay per hour worked
                        </Typography>
                      </Box>
                      
                      {isHourly && (
                        <Grid container spacing={2}>
                          <Grid xs={6}>
                            <FormControl>
                              <FormLabel sx={{ fontWeight: 500, fontSize: '0.875rem', color: '#1A1B1D', marginBottom: '4px' }}>
                                Min Hourly Rate
                              </FormLabel>
                              <Input
                                value={hourlyMin}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHourlyMin(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                placeholder="e.g. 25"
                                type="number"
                                sx={{
                                  height: '48px',
                                  backgroundColor: '#FFFFFF',
                                  borderRadius: '8px',
                                  border: '1px solid #E5E7EB',
                                  fontSize: '1rem',
                                  color: '#1A1B1D',
                                  '--Input-focusedHighlight': 'transparent !important',
                                  '&::placeholder': {
                                    color: '#7B7F85 !important',
                                    opacity: 1,
                                  },
                                  '&:hover': {
                                    backgroundColor: '#FFFFFF',
                                  },
                                  '&:focus': {
                                    outline: 'none',
                                  },
                                  '&:focus-within': {
                                    backgroundColor: '#FFFFFF',
                                  },
                                }}
                                startDecorator="$ "
                              />
                            </FormControl>
                          </Grid>
                          <Grid xs={6}>
                            <FormControl>
                              <FormLabel sx={{ fontWeight: 500, fontSize: '0.875rem', color: '#1A1B1D', marginBottom: '4px' }}>
                                Max Hourly Rate
                              </FormLabel>
                              <Input
                                value={hourlyMax}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHourlyMax(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                placeholder="e.g. 35"
                                type="number"
                                sx={{
                                  height: '48px',
                                  backgroundColor: '#FFFFFF',
                                  borderRadius: '8px',
                                  border: '1px solid #E5E7EB',
                                  fontSize: '1rem',
                                  color: '#1A1B1D',
                                  '--Input-focusedHighlight': 'transparent !important',
                                  '&::placeholder': {
                                    color: '#7B7F85 !important',
                                    opacity: 1,
                                  },
                                  '&:hover': {
                                    backgroundColor: '#FFFFFF',
                                  },
                                  '&:focus': {
                                    outline: 'none',
                                  },
                                  '&:focus-within': {
                                    backgroundColor: '#FFFFFF',
                                  },
                                }}
                                startDecorator="$ "
                              />
                            </FormControl>
                          </Grid>
                        </Grid>
                      )}
                    </Box>

                    <Box
                      onClick={() => setReceivesTips(!receivesTips)}
                      sx={{
                        width: '100%',
                        padding: '20px',
                        borderRadius: '12px',
                        backgroundColor: receivesTips ? '#F0F2F5' : '#F8F9FA',
                        border: receivesTips ? '2px solid #2e2d7e' : '1px solid #E5E7EB',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          borderColor: '#2e2d7e',
                          backgroundColor: receivesTips ? '#E8EAED' : '#F0F2F5',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography level="h4" sx={{ fontWeight: 600, color: '#1A1B1D', fontSize: '1.125rem' }}>
                          Tips
                        </Typography>
                        <Typography level="body-sm" sx={{ color: '#7B7F85', fontSize: '0.875rem', ml: 2 }}>
                          This position may receive tips
                        </Typography>
                      </Box>
                    </Box>
                  </Stack>
                </FormControl>

                {/* Position Description */}
                <FormControl>
                  <FormLabel sx={{ fontWeight: 500, fontSize: '1rem', color: '#1A1B1D', marginBottom: '8px' }}>
                    Position Description
                  </FormLabel>
                  <Textarea
                    value={positionDescription}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setPositionDescription(e.target.value)}
                    placeholder="Describe the position, responsibilities, and requirements..."
                    minRows={4}
                    sx={textareaStyles}
                  />
                  <Typography level="body-sm" sx={{ mt: 1, color: '#7B7F85' }}>
                    {positionDescription.length} characters (recommended: 100-200)
                  </Typography>
                </FormControl>

                {/* Position Media - Mobile Only */}
                <FormControl sx={{ display: { xs: 'block', md: 'none' } }}>
                  <FormLabel sx={{ fontWeight: 500, fontSize: '1rem', color: '#1A1B1D', marginBottom: '8px' }}>
                    Position Media
                  </FormLabel>
                  <Box>
                    {videoUrl ? (
                      <Box sx={{ 
                        width: '100%',
                        aspectRatio: '3/4',
                        backgroundColor: '#000',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        position: 'relative',
                        mb: 2
                      }}>
                        <GeekPlayer
                          localVideoUrl={videoUrl}
                          playing={true}
                        />
                        <Button
                          size="sm"
                          variant="plain"
                          color="danger"
                          sx={{ 
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'rgba(0,0,0,0.9)',
                            }
                          }}
                          onClick={() => {
                            setPositionMedia(null);
                            setVideoUrl('');
                            URL.revokeObjectURL(videoUrl);
                          }}
                        >
                          Remove
                        </Button>
                      </Box>
                    ) : (
                      <>
                        <input
                          type="file"
                          accept="video/*"
                          onChange={handleVideoUpload}
                          style={{ display: 'none' }}
                          id="position-media-upload-mobile"
                        />
                        <label htmlFor="position-media-upload-mobile">
                          <Button
                            component="span"
                            variant="outlined"
                            fullWidth
                            sx={{ 
                              height: '56px',
                              fontSize: '1.125rem',
                              borderColor: '#ECEFF3',
                              color: '#1A1B1D',
                              backgroundColor: '#ECEFF3',
                              '&:hover': {
                                backgroundColor: '#E0E3E7',
                                borderColor: '#E0E3E7',
                              }
                            }}
                          >
                            Upload Position Media
                          </Button>
                        </label>
                      </>
                    )}
                    
                    {isUploading && (
                      <Box sx={{ mt: 2 }}>
                        <Typography level="body-sm" sx={{ color: '#2e2d7e', mb: 1, fontWeight: 500 }}>
                          Do not navigate to a new page while your video is uploading
                        </Typography>
                        <LinearProgress 
                          variant="solid"
                          determinate
                          value={uploadProgress} 
                          sx={{ 
                            backgroundColor: '#E0E3E7',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: '#2e2d7e',
                            }
                          }}
                        />
                        <Typography level="body-sm" sx={{ mt: 1, color: '#7B7F85' }}>
                          {uploadProgress}% uploaded
                        </Typography>
                      </Box>
                    )}
                    
                    <Typography level="body-sm" sx={{ mt: 1, color: '#7B7F85' }}>
                      Recommended: 1080p, 9:16 aspect ratio, 90 seconds or less, MP4/MOV/WEBM
                    </Typography>
                  </Box>
                </FormControl>

                {/* Save Button - Mobile Only */}
                <Button
                  variant="solid"
                  fullWidth
                  onClick={handleSave}
                  disabled={saving || isUploading || !!slugError}
                  sx={{
                    height: '56px',
                    fontSize: '18px',
                    fontWeight: 600,
                    marginTop: '24px',
                    backgroundColor: '#2e2d7e',
                    '&:hover': { backgroundColor: '#1e1d6e' },
                    display: { xs: 'block', md: 'none' }
                  }}
                >
                  {saving ? 'Saving...' : isUploading ? 'Uploading...' : 'Save Position'}
                </Button>

                {/* Delete Button - Mobile Only */}
                {applicantCount === 0 && (
                  <Button
                    variant="plain"
                    color="danger"
                    fullWidth
                    onClick={handleDelete}
                    disabled={deleting || isUploading}
                    sx={{
                      height: '56px',
                      fontSize: '18px',
                      fontWeight: 600,
                      marginTop: '12px',
                      display: { xs: 'block', md: 'none' },
                      borderColor: "#2E2D7E",
                      color: "#2E2D7E",
                      borderRadius: "8px",
                      "&:hover": {
                        borderColor: "#1a1a5e",
                        color: "#1a1a5e",
                        bgcolor: "rgba(46, 45, 126, 0.04)",
                      },
                    }}
                  >
                    {deleting ? 'Deleting...' : 'Delete Post'}
                  </Button>
                )}
              </Stack>
            </Grid>

            {/* Right Column - Video Upload */}
            <Grid xs={12} md={4}>
              <Box sx={{ 
                display: { xs: 'none', md: 'block' },
                height: '100%',
                minHeight: '400px'
              }}>
                <Typography sx={{ fontWeight: 500, fontSize: '1rem', color: '#1A1B1D', marginBottom: '8px' }}>
                  Position Media
                </Typography>
                
                {videoUrl ? (
                  <Box sx={{ 
                    width: '100%',
                    aspectRatio: '3/4',
                    backgroundColor: '#000',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    <GeekPlayer
                      localVideoUrl={videoUrl}
                      playing={true}
                    />
                    <Button
                      size="sm"
                      variant="plain"
                      color="danger"
                      sx={{ 
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        backgroundColor: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.9)',
                        }
                      }}
                      onClick={() => {
                        setPositionMedia(null);
                        setVideoUrl('');
                        URL.revokeObjectURL(videoUrl);
                      }}
                    >
                      Remove
                    </Button>
                  </Box>
                ) : showExistingVideo ? (
                  <Box sx={{ 
                    width: '100%',
                    aspectRatio: '3/4',
                    backgroundColor: '#000',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    position: 'relative',
                    border: '2px dashed #7B7F85',
                    '&:hover': {
                      borderColor: '#2e2d7e'
                    }
                  }}
                  onClick={() => document.getElementById('position-media-upload-desktop')?.click()}
                >
                  <GeekPlayer
                    key={existingMediaId || 'no-media'}
                    videoId={existingMediaId || ''}
                    playing={true}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(0,0,0,0.7)',
                      color: 'white',
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                      pointerEvents: 'none'
                    }}
                  >
                    <Typography level="body-sm">Click to replace video</Typography>
                  </Box>
                  <input
                    type="file"
                    accept="video/*"
                    onChange={handleVideoUpload}
                    style={{ display: 'none' }}
                    id="position-media-upload-desktop"
                  />
                </Box>
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      aspectRatio: '3/4',
                      backgroundColor: '#ECEFF3',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px dashed #7B7F85',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#E0E3E7',
                        borderColor: '#2e2d7e'
                      }
                    }}
                    onClick={() => document.getElementById('position-media-upload-desktop')?.click()}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                  >
                    <input
                      type="file"
                      accept="video/*"
                      onChange={handleVideoUpload}
                      style={{ display: 'none' }}
                      id="position-media-upload-desktop"
                    />
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography level="body-sm" sx={{ color: '#7B7F85', mb: 1 }}>
                        Drag & drop video here
                      </Typography>
                      <Typography level="body-xs" sx={{ color: '#7B7F85' }}>
                        or click to browse
                      </Typography>
                    </Box>
                  </Box>
                )}
                <Typography level="body-sm" sx={{ mt: 1, color: '#7B7F85' }}>
                  Recommended: 1080p, 9:16 aspect ratio, 90 seconds or less, MP4/MOV/WEBM
                </Typography>
                
                {isUploading && (
                  <Box sx={{ mt: 2 }}>
                    <Typography level="body-sm" sx={{ color: '#2e2d7e', mb: 1, fontWeight: 500 }}>
                      Do not navigate to a new page while your video is uploading
                    </Typography>
                    <LinearProgress 
                      variant="solid"
                      determinate
                      value={uploadProgress} 
                      sx={{ 
                        backgroundColor: '#E0E3E7',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: '#2e2d7e',
                        }
                      }}
                    />
                    <Typography level="body-sm" sx={{ mt: 1, color: '#7B7F85' }}>
                      {uploadProgress}% uploaded
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>

          {/* Save Button - Desktop Only */}
          <Box sx={{ 
            display: { xs: 'none', md: 'flex' },
            justifyContent: 'flex-start',
            gap: 2,
            mt: 3
          }}>
            <Button
              variant="solid"
              onClick={handleSave}
              disabled={saving || isUploading || !!slugError}
              sx={{
                height: '56px',
                fontSize: '18px',
                fontWeight: 600,
                px: 4,
                backgroundColor: '#2e2d7e',
                '&:hover': { backgroundColor: '#1e1d6e' },
                '&:disabled': { backgroundColor: '#7B7F85' }
              }}
            >
              {saving ? 'Saving...' : isUploading ? 'Uploading...' : 'Save Position'}
            </Button>

            {/* Delete Button - Desktop Only */}
            {applicantCount === 0 && (
              <Button
                variant="plain"
                color="danger"
                onClick={handleDelete}
                disabled={deleting || isUploading}
                sx={{
                  height: '56px',
                  fontSize: '18px',
                  fontWeight: 600,
                  px: 4,
                  borderColor: "#2E2D7E",
                      color: "#2E2D7E",
                      borderRadius: "8px",
                      "&:hover": {
                        borderColor: "#1a1a5e",
                        color: "#1a1a5e",
                        bgcolor: "rgba(46, 45, 126, 0.04)",
                      },
                }}
              >
                {deleting ? 'Deleting...' : 'Delete Post'}
              </Button>
            )}
          </Box>
        </Sheet>
      </Container>
    </Box>
  );
};

export default EditPositionListingPage; 